import {Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Switch, Table, Tooltip} from "antd";
import React, {useEffect, useState} from "react";
import {DownloadOutlined, InfoCircleOutlined, SearchOutlined} from "@ant-design/icons";
import type {ColumnsType, TablePaginationConfig} from "antd/es/table";
import dayjs from "dayjs";
import {InstallmentReportDTO, PAYMENT_TYPE_LABEL, PaymentType} from "../../../model/models";
import {HttpErrorService} from "../../../services/http/HttpError.service";
import {PracticeExportService} from "../../practices/PracticeExport.service";
import {HttpService} from "../../../services/http/Http.service";
import {UtilsService} from "../../../services/Utils.service";

export default function ReportRate() {
    const [filteredData, setFilteredData] = useState<InstallmentReportDTO[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [form] = Form.useForm();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const [summary, setSummary] = useState({
        totalAssisted: 0,
        totalGranted: 0,
        totalPaid: 0,
        totalToBePaid: 0,
    });

    useEffect(() => {
        fetchData({}, { current: currentPage, pageSize });
    }, []);

    const fetchData = async (
        values?: any,
        pagination?: TablePaginationConfig,
        sorter?: any
    ) => {
        setLoading(true);
        try {
            const data = await getInstallmentReportData(
                values,
                { page: (pagination?.current || 1) - 1, size: pagination?.pageSize || 20 },
                sorter,
                { includeAllPaid: false }
            );

            setFilteredData(data.content || []);
            setTotalItems(data.totalElements);
            setCurrentPage(data.number + 1);
            setPageSize(data.size);
            setSummary({
                totalAssisted: data.totalAssisted,
                totalGranted: data.totalGranted,
                totalPaid: data.totalPaid,
                totalToBePaid: data.totalToBePaid,
            });
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero dei dati del report rate", error, true);
        } finally {
            setLoading(false);
        }
    };


    const handleOnFinish = (values: any) => {
        fetchData(values, { current: 1, pageSize });
    };

    const handleExportRate = async () => {
        setExporting(true);
        try {
            const values = form.getFieldsValue();
            await PracticeExportService.loadAndExportToExcelRate(
                buildReportPayload(values),
                isTodayOnly(values),
                true,
                "report_rate.xlsx"
            );
        } catch (error) {
            HttpErrorService.handle("Errore durante l'esportazione", error, true);
        } finally {
            setExporting(false);
        }
    };

    const isTodayOnly = (values: any) => !values || Object.keys(values).length === 0;

    const buildReportPayload = (values: any) => ({
        ...values,
        amountFrom: values?.amountFrom ? dayjs(values.amountFrom).format("YYYY-MM-DD") : null,
        amountTo: values?.amountTo ? dayjs(values.amountTo).format("YYYY-MM-DD") : null,
    });

    const getInstallmentReportData = async (
        values: any,
        pagination: { page: number; size: number },
        sorter?: any,
        opts?: { includeAllPaid?: boolean }
    ) => {
        const todayOnly = isTodayOnly(values);

        const payload = buildReportPayload(values);

        const params: Record<string, any> = {
            page: pagination.page,
            size: pagination.size,
        };
        if (sorter?.field) {
            params.sort = `${sorter.field},${
                sorter.order === "descend" ? "desc" : "asc"
            }`;
        }

        const includeAllPaid = opts?.includeAllPaid ? "true" : "false";

        const res = await HttpService.getInstanceAxios().post(
            `/installment/report-rate?todayOnly=${todayOnly}&includeAllPaid=${includeAllPaid}`,
            payload,
            { params }
        );

        return res.data;
    };

    const columns: ColumnsType<InstallmentReportDTO> = [
        { title: "Codice fiscale", dataIndex: "fiscalCode", key: "fiscalCode", width: 150, sorter: true },
        { title: "Cognome", dataIndex: "lastName", key: "lastName", width: 150, sorter: true },
        { title: "Nome", dataIndex: "firstName", key: "firstName", width: 150 },
        { title: "Cod. esenzione", dataIndex: "exemptionCodes", key: "exemptionCodes", width: 150 },
        { title: "Protocollo archiviazione", dataIndex: "archiveProtocol", key: "archiveProtocol", width: 180 },
        {
            title: "Num. rata",
            key: "installmentOrder",
            width: 120,
            render: (_, record) => {
                if (record.missedPaymentApplied) {
                    return (
                        <span>
          {record.totalInstallments}
                            <Tooltip title="Mancato pagamento">
            <InfoCircleOutlined style={{ color: "orange", marginLeft: 6,fontSize: 10  }} />
          </Tooltip>
        </span>
                    );
                }
                return `${record.allInstallmentsPaid ? record.totalInstallments : record.installmentOrder}/${record.totalInstallments}`;
            },
        },
        {
            title: "Importo rata",
            key: "totalInstallmentAmount",
            render: (_, record) => `€ ${UtilsService.formatNumber(record.totalInstallmentAmount)}`,
        },
        {
            title: "Giorni",
            key: "daysLabel",
            width: 180,
            render: (_, record) => {
                const days = record.daysUntilDue;
                const isPaid = record.allInstallmentsPaid;
                let text = "-";
                let color = "";
                if (isPaid) {
                    text = "Pagate";
                    color = "#389e0d";
                } else if (days !== undefined) {
                    if (days < 0) {
                        text = `Scaduta da ${Math.abs(days)} giorni`;
                        color = "#cf1322";
                    } else if (days <= 60) {
                        text = `Mancano ${days} giorni`;
                        color = "#d48806";
                    } else {
                        text = `Mancano ${days} giorni`;
                        color = "#096dd9";
                    }
                }
                return <span style={{ color, fontWeight: "bold" }}>{text}</span>;
            },
        },
        {
            title: "Data scadenza",
            dataIndex: "deadlineDate",
            key: "deadlineDate",
            width: 150,
            sorter: true,
            render: (date: string) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Data pagamento",
            dataIndex: "paymentDate",
            key: "paymentDate",
            width: 150,
            render: (date: string) => (date ? UtilsService.formatDateString(date) : "-"),
        },
        {
            title: "Tipo pagamento",
            dataIndex: "paymentType",
            key: "paymentType",
            width: 150,
            render: (type: PaymentType) => type ? (PAYMENT_TYPE_LABEL[type] ?? type) : "-",
        },
    ];

    return (
        <>
            <Form form={form} layout="vertical" onFinish={handleOnFinish}>
                <Row gutter={16} align="bottom">
                    <Col>
                        <Form.Item label="Giorni al pagamento">
                            <Input.Group compact>
                                <Form.Item name="fromDays" noStyle>
                                    <InputNumber placeholder="Da" />
                                </Form.Item>
                                <Form.Item name="toDays" noStyle>
                                    <InputNumber placeholder="A" />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item label="Situazione nel periodo">
                            <Input.Group compact>
                                <Form.Item name="amountFrom" noStyle>
                                    <DatePicker format="DD-MM-YYYY" placeholder="Da" />
                                </Form.Item>
                                <Form.Item name="amountTo" noStyle>
                                    <DatePicker format="DD-MM-YYYY" placeholder="A" />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item name="scadute" label="Scadute" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item name="pagate" label="Pagate" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item name="searchText" label="Cerca">
                            <Input placeholder="Nome, Cognome, Codice Fiscale" />
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item>
                            <Button icon={<SearchOutlined />} type="primary" htmlType="submit" />
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item>
                            <Button type="primary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExportRate}>
                                Genera report
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <Card
                title={"Statistiche"}
                className="header-card"
                styles={{ header: { background: "#007A33FF", color: "white" } }}
            >
                <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "20px", justifyContent: "space-between" }}
                >
                    <div>
                        <b>N° assistiti con rate:</b> {summary.totalAssisted}
                    </div>
                    <div>
                        <b>Importo rate concesse:</b> € {UtilsService.formatNumber(summary.totalGranted)}
                    </div>
                    <div>
                        <b>Importo rate pagate:</b> € {UtilsService.formatNumber(summary.totalPaid)}
                    </div>
                    <div>
                        <b>Importo rate da pagare:</b> € {UtilsService.formatNumber(summary.totalToBePaid)}
                    </div>
                </div>
            </Card>

            <Table
                rowKey={(record) => `${record.fiscalCode}-${record.installmentOrder}`}
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalItems,
                    showSizeChanger: true,
                    onChange: (page, size) =>
                        fetchData(form.getFieldsValue(), { current: page, pageSize: size }),
                }}
                bordered
                size="small"
                scroll={{ x: "max-content" }}
                onChange={(pagination, _filters, sorter) => {
                    if (!Array.isArray(sorter) && sorter.columnKey) {
                        fetchData(form.getFieldsValue(), pagination, sorter);
                    }
                }}
            />
        </>
    );
}
